/**
 * CampaignSwitchManager
 *
 * Responsibilities:
 * - Track pending campaign switches with timeouts
 * - Handle pavilionLaunchReply results (SUCCESS, FAILURE, etc.)
 * - Report success/failure to API
 * - Manage timeout logic (base timeout + game-specific ready wait time)
 *
 * Pattern: Single-responsibility manager
 *
 * This is the new architecture equivalent of campaignSwitchTracker.ts
 */

import type { GameLaunchStores } from '../stores';
import { CAMPAIGN_SWITCH_BASE_TIMEOUT_MS } from '@playcastdotio/Messaging';
import type { PavilionLauncResults } from '@playcastdotio/CoreTypes';
import * as Sentry from '@sentry/react';

export type CampaignSwitchResult =
  | { success: true }
  | { success: false; reason: string; transient?: boolean };

type PendingSwitch = {
  campaignId: string;
  resolve: (result: CampaignSwitchResult) => void;
  timeoutId: ReturnType<typeof setTimeout>;
  startTime: number;
};

/**
 * Campaign launch timeout configuration
 * - Base timeout: 480000ms (8 minutes) — shared with the Wait-node poll cap
 * - Plus game-specific ready wait time
 */

export class CampaignSwitchManager {
  private pendingSwitch: PendingSwitch | null = null;
  private pendingPromise: Promise<CampaignSwitchResult> | null = null;
  private settleGuard: (() => boolean) | null = null;

  constructor(
    private stores: GameLaunchStores
  ) {}

  init(): void {
    console.log('[CampaignSwitchManager] Initialized');
    Sentry.logger.info('[CampaignSwitchManager] Initialized');
  }

  /**
   * Register a guard function that returns true while the walk is in a
   * legitimate long settle. The timeout handler checks this before firing.
   */
  public setSettleGuard(guard: () => boolean): void {
    this.settleGuard = guard;
  }

  /**
   * Start tracking a campaign switch with timeout
   *
   * @param campaignId The campaign being switched to
   * @param readyWaitTimeMs Game-specific ready wait time (from launch package)
   * @returns Promise that resolves when result is received or timeout fires
   */
  startSwitch(campaignId: string, readyWaitTimeMs = 0): Promise<CampaignSwitchResult> {
    if (this.pendingSwitch && this.pendingPromise) {
      if (this.pendingSwitch.campaignId === campaignId) {
        console.warn(`[CampaignSwitchManager] Duplicate startSwitch for "${campaignId}" — returning existing promise`);
        Sentry.logger.warn(`[CampaignSwitchManager] Duplicate startSwitch for "${campaignId}" — returning existing promise`);
        return this.pendingPromise;
      }

      console.warn(`[CampaignSwitchManager] Clearing pending switch for "${this.pendingSwitch.campaignId}" — superseded by "${campaignId}"`);
      Sentry.logger.warn(`[CampaignSwitchManager] Clearing pending switch for "${this.pendingSwitch.campaignId}" — superseded by "${campaignId}"`);
      clearTimeout(this.pendingSwitch.timeoutId);
      this.pendingSwitch.resolve({ success: false, reason: 'campaign_switch_superseded', transient: true });
    }

    const totalTimeoutMs = CAMPAIGN_SWITCH_BASE_TIMEOUT_MS + readyWaitTimeMs;

    this.pendingPromise = new Promise<CampaignSwitchResult>((resolve) => {
      const startTime = Date.now();

      const timeoutId = setTimeout(() => {
        if (this.settleGuard?.()) {
          console.log('[CampaignSwitchManager] Timeout would fire but settle guard is active — deferring');
          Sentry.logger.info('[CampaignSwitchManager] Timeout deferred due to active settle guard');
          const extendedTimeout = setTimeout(() => {
            const elapsed = Date.now() - startTime;
            console.error(`[CampaignSwitchManager] ⏰ TIMEOUT fired after ${elapsed}ms (settle guard extended)`);
            Sentry.logger.error(`[CampaignSwitchManager] ⏰ TIMEOUT fired after ${elapsed}ms (settle guard extended)`);
            this.pendingSwitch = null;
            resolve({ success: false, reason: 'campaign_switch_timeout' });
          }, 600_000);
          if (this.pendingSwitch) {
            this.pendingSwitch.timeoutId = extendedTimeout;
          }
          return;
        }
        const elapsed = Date.now() - startTime;
        console.error(`[CampaignSwitchManager] ⏰ TIMEOUT fired after ${elapsed}ms (expected ${totalTimeoutMs}ms)`);
        Sentry.logger.error(`[CampaignSwitchManager] ⏰ TIMEOUT fired after ${elapsed}ms (expected ${totalTimeoutMs}ms)`);
        this.pendingSwitch = null;
        resolve({ success: false, reason: 'campaign_switch_timeout' });
      }, totalTimeoutMs);

      this.pendingSwitch = {
        campaignId,
        resolve,
        timeoutId,
        startTime,
      };

      console.log(`[CampaignSwitchManager] ⏱️  Started tracking campaign switch: "${campaignId}" with ${totalTimeoutMs}ms timeout`);
      Sentry.logger.info(`[CampaignSwitchManager] ⏱️  Started tracking campaign switch: "${campaignId}" with ${totalTimeoutMs}ms timeout`);
    });

    return this.pendingPromise;
  }

  /**
   * Report pavilionLaunchReply result from Playjector
   *
   * Called when IPC message is received with launch result
   *
   * @param result SUCCESS | FAILURE | SECURITY_FAILURE | PATH_DISCREPANCY | PID_DISCREPANCY
   * @param reason Optional failure reason message
   */
  reportResult(result: PavilionLauncResults, reason?: string): void {
    if (!this.pendingSwitch) {
      console.warn(`[CampaignSwitchManager] ⚠️  Received pavilionLaunchReply (${result}) but no pending switch - timeout already fired?`);
      Sentry.logger.warn(`[CampaignSwitchManager] ⚠️  Received pavilionLaunchReply (${result}) but no pending switch - timeout already fired?`);
      return;
    }

    const elapsed = Date.now() - this.pendingSwitch.startTime;
    clearTimeout(this.pendingSwitch.timeoutId);
    console.log(`[CampaignSwitchManager] ✅ reportResult called after ${elapsed}ms, timeout cleared. Result: ${result}`);
    Sentry.logger.info(`[CampaignSwitchManager] ✅ reportResult called after ${elapsed}ms, timeout cleared. Result: ${result}`);

    if (result === 'SUCCESS') {
      console.log('[CampaignSwitchManager] 🎉 Game launch SUCCESS - resolving promise');
      Sentry.logger.info('[CampaignSwitchManager] 🎉 Game launch SUCCESS - resolving promise');
      this.pendingSwitch.resolve({ success: true });
    } else {
      console.error('[CampaignSwitchManager] ❌ Game launch failed:', result, reason || '');
      Sentry.logger.error('[CampaignSwitchManager] ❌ Game launch failed:', { result, reason: reason || '' });
      this.pendingSwitch.resolve({
        success: false,
        reason: reason || result
      });
    }

    this.pendingSwitch = null;
    this.pendingPromise = null;
  }

  /**
   * Cancel pending switch (e.g., on disconnect or manual cancellation)
   */
  cancelPendingSwitch(reason = 'host_campaign_pending_cancelled'): void {
    if (!this.pendingSwitch) {
      return;
    }

    console.log('[CampaignSwitchManager] Cancelling pending switch:', reason);
    Sentry.logger.info('[CampaignSwitchManager] Cancelling pending switch:', { reason });
    clearTimeout(this.pendingSwitch.timeoutId);
    this.pendingSwitch.resolve({ success: false, reason });
    this.pendingSwitch = null;
    this.pendingPromise = null;
  }

  /**
   * Check if there's a pending switch waiting for result
   */
  hasPendingSwitch(): boolean {
    return this.pendingSwitch !== null;
  }

  /**
   * Get the campaign ID of the pending switch (if any)
   */
  getPendingCampaignId(): string | null {
    return this.pendingSwitch?.campaignId || null;
  }
}